import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Breadcrumb from '@/components/common/Breadcrumb';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Settings as SettingsIcon,
  Users,
  Warehouse,
  Link2,
  Shield,
  Clock,
  Bell,
  Package,
  Layers,
  Calendar,
  FileText,
  ChevronRight,
  Folder,
  User,
  BookOpen,
  Mail,
  ExternalLink,
  BarChart,
  Search,
  Grid3X3,
  List,
  TrendingUp,
  Activity,
  Zap,
  Filter,

  CheckCircle2,
  ArrowRight
} from 'lucide-react';

interface SettingsMenuItem {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  href: string;
  category: 'general' | 'business' | 'contacts' | 'education' | 'communication' | 'templates';
  isNew?: boolean;

}

const settingsMenuItems: SettingsMenuItem[] = [
  // Configurações Gerais
  {
    id: 'dashboard',
    title: 'Dashboard',
    description: 'Métricas e relatórios do sistema',
    icon: BarChart,
    href: '/dashboard',
    category: 'general'
  },
  {
    id: 'users',
    title: 'Usuários',
    description: 'Gerenciar usuários do sistema',
    icon: Users,
    href: '/users',
    category: 'general'
  },
  {
    id: 'vendors',
    title: 'Fornecedores',
    description: 'Gerenciar fornecedores e parceiros',
    icon: Warehouse,
    href: '/vendors',
    category: 'general'
  },
  {
    id: 'integrations',
    title: 'Integrações',
    description: 'Conexões com sistemas externos',
    icon: Link2,
    href: '/integrations',
    category: 'general'
  },
  {
    id: 'access-control',
    title: 'Controle de Acesso',
    description: 'Permissões e controle de acesso',
    icon: Shield,
    href: '/access-control',
    category: 'general'
  },
  {
    id: 'system-jobs',
    title: 'System Jobs',
    description: 'Tarefas e processos do sistema',
    icon: Clock,
    href: '/system-jobs',
    category: 'general'
  },
  {
    id: 'system-notifications',
    title: 'Notificações do Sistema',
    description: 'Configurar alertas e notificações',
    icon: Bell,
    href: '/system-notifications',
    category: 'general',
    isNew: true
  },
  // Configurações de Negócio
  {
    id: 'subscription-plans',
    title: 'Planos de Assinatura',
    description: 'Gerenciar planos e preços',
    icon: Package,
    href: '/subscription-plans',
    category: 'business'
  },
  {
    id: 'subscription-plan-families',
    title: 'Famílias de Planos',
    description: 'Categorias e grupos de planos',
    icon: Layers,
    href: '/subscription-plan-families',
    category: 'business'
  },
  {
    id: 'economic-indices',
    title: 'Índices Econômicos',
    description: 'Gerenciar índices econômicos para assinaturas',
    icon: BarChart,
    href: '/economic-indices',
    category: 'business'
  },
  // Configurações de Educação
  {
    id: 'course-families',
    title: 'Famílias de Cursos',
    description: 'Gerenciar categorias e grupos de cursos',
    icon: Folder,
    href: '/course-families',
    category: 'education'
  },
  {
    id: 'instructors',
    title: 'Instrutores',
    description: 'Gerenciar instrutores e professores',
    icon: User,
    href: '/instructors',
    category: 'education'
  },
  // Configurações de Comunicação
  {
    id: 'transactional-emails',
    title: 'Emails Transacionais',
    description: 'Gerenciar emails transacionais do sistema',
    icon: Mail,
    href: '/transactional-emails',
    category: 'communication'
  },
  {
    id: 'templates-email',
    title: 'Templates Email',
    description: 'Visualizar e testar templates de email',
    icon: ExternalLink,
    href: '/templates-email',
    category: 'communication'
  },
  // Templates
  {
    id: 'proposal-templates',
    title: 'Templates de Proposta',
    description: 'Gerenciar templates de propostas comerciais',
    icon: FileText,
    href: '/proposal-templates',
    category: 'templates'
  },
  {
    id: 'contract-templates',
    title: 'Templates de Contrato',
    description: 'Gerenciar templates de contratos',
    icon: FileText,
    href: '/contract-templates',
    category: 'templates'
  }
];

const categoryTitles = {
  general: 'Configurações Gerais',
  business: 'Configurações de Negócio',
  contacts: 'Contatos',
  education: 'Configurações de Educação',
  communication: 'Configurações de Comunicação',
  templates: 'Templates'
};

interface SettingsSummary {
  totalSettings: number;
  activeIntegrations: number;
  totalUsers: number;
  totalCategories: number;
}

export default function Settings() {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [summaryData, setSummaryData] = useState<SettingsSummary>({
    totalSettings: settingsMenuItems.length,
    activeIntegrations: 4,
    totalUsers: 12,
    totalCategories: Object.keys(categoryTitles).length
  });

  const groupedItems = settingsMenuItems.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {} as Record<string, SettingsMenuItem[]>);

  const filteredItems = settingsMenuItems.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });



  const handleItemClick = (href: string) => {
    navigate(href);
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'general': return SettingsIcon;
      case 'business': return Package;
      case 'education': return BookOpen;
      case 'communication': return Mail;
      case 'templates': return FileText;
      default: return SettingsIcon;
    }
  };

  return (
    <>
      <Helmet>
        <title>CSM | Trivium - Configurações</title>
      </Helmet>
      
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
        {/* Modern Header with Gradient Background */}
        <div className="relative overflow-hidden" style={{ backgroundColor: '#1F1746' }}>
          {/* SVG Background Element */}
          <div className="absolute inset-0 pointer-events-none">
            <svg 
              className="opacity-30"
              width="1690"
              height="1026.5433"
              viewBox="0 0 826.54 502.06" 
              xmlns="http://www.w3.org/2000/svg"
              preserveAspectRatio="xMidYMid slice"
            >
              <g transform="scale(1.1) translate(-70, -315)">
                <path d="M0 0h826.54v502.06H0z" fill="none"/>
                <path d="M0 0h826.54v502.06H0z" fill="url(#a)"/>
                <defs>
                  <linearGradient id="a" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity=".1"/>
                    <stop offset="100%" stopColor="#8B5CF6" stopOpacity=".1"/>
                  </linearGradient>
                </defs>
              </g>
            </svg>
          </div>
          <div className="absolute inset-0 bg-black/20" />
          <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
          
          <div className="relative container mx-auto px-6 py-8">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <SettingsIcon className="h-7 w-7 text-white" />
                  </div>
                  <div>
                    <h1 className="text-3xl font-extrabold text-white">Configurações do Sistema</h1>
                    <p className="text-blue-100 text-lg">
                      Central de controle e personalização da plataforma
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button 
                  variant="secondary" 
                  size="default" 
                  onClick={() => navigate('/system-backup')} 
                  className="backdrop-blur-sm bg-white/20 border-white/30 text-white hover:bg-white/30 transition-all duration-200"
                >
                  <Shield className="h-4 w-4 mr-2" />
                  Backup do Sistema
                </Button>
                <Button 
                  size="default" 
                  onClick={() => navigate('/system-health')} 
                  className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
                >
                  <Activity className="h-4 w-4 mr-2" />
                  Status do Sistema
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="pl-3 lg:pl-4">
          <div className="container mx-auto px-6 -mt-4 relative z-10">
            {/* Enhanced Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-blue-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <SettingsIcon className="h-5 w-5 text-blue-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Total Configurações</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.totalSettings}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Em {summaryData.totalCategories} categorias
                    </div>
                    <div className="flex items-center gap-1">
                      <TrendingUp className="h-3 w-3 text-blue-500" />
                      <span className="text-xs text-blue-600 font-extrabold">100%</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-green-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Link2 className="h-5 w-5 text-green-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Integrações Ativas</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.activeIntegrations}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Sistemas conectados
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-green-500" />
                      <span className="text-xs text-green-600 font-extrabold">Online</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-purple-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-purple-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Users className="h-5 w-5 text-purple-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Usuários Ativos</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.totalUsers}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      No sistema
                    </div>
                    <div className="flex items-center gap-1">
                      <Activity className="h-3 w-3 text-purple-500" />
                      <span className="text-xs text-purple-600 font-extrabold">+2</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="relative overflow-hidden border shadow-sm bg-gradient-to-br from-orange-50 to-white">
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-orange-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-orange-100 rounded-lg">
                        <Zap className="h-5 w-5 text-orange-600" />
                      </div>
                      <span className="text-sm font-extrabold text-gray-600">Configurações Ativas</span>
                    </div>
                    <div className="text-2xl font-extrabold text-gray-900">
                      {summaryData.totalSettings}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      Disponíveis no sistema
                    </div>
                    <div className="flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3 text-orange-500" />
                      <span className="text-xs text-orange-600 font-extrabold">Ativo</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Enhanced Main Content */}
            <Card className="border shadow-sm bg-white/80 backdrop-blur-sm">
              <CardContent className="p-8">
                {/* Enhanced Filter Section */}
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 mb-8">
                  <div className="flex items-center gap-4 flex-1 w-full lg:w-auto">
                    <div className="relative flex-1 max-w-md">
                      <Search className="absolute left-3 top-1/2 h-4 w-4 text-gray-400 transform -translate-y-1/2" />
                      <Input
                        placeholder="Pesquisar configurações..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 h-11 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <Tabs value={selectedCategory} onValueChange={setSelectedCategory} className="w-auto">
                      <TabsList className="grid grid-cols-6 bg-gray-100">
                        <TabsTrigger value="all" className="text-xs">Todos</TabsTrigger>
                        <TabsTrigger value="general" className="text-xs">Geral</TabsTrigger>
                        <TabsTrigger value="business" className="text-xs">Negócio</TabsTrigger>
                        <TabsTrigger value="education" className="text-xs">Educação</TabsTrigger>
                        <TabsTrigger value="communication" className="text-xs">Comunicação</TabsTrigger>
                        <TabsTrigger value="templates" className="text-xs">Templates</TabsTrigger>
                      </TabsList>
                    </Tabs>

                    <div className="flex bg-gray-100 rounded-lg p-1">
                      <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 rounded-md transition-colors ${
                          viewMode === 'grid'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <Grid3X3 className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 rounded-md transition-colors ${
                          viewMode === 'list'
                            ? 'bg-white text-gray-900 shadow-sm'
                            : 'text-gray-600 hover:text-gray-900'
                        }`}
                      >
                        <List className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>



                {/* Settings Grid/List */}
                <div className="space-y-8">
                  {selectedCategory === 'all' && !searchTerm ? (
                    Object.entries(groupedItems).map(([category, items]) => (
                      <div key={category} className="space-y-6">
                        <div className="flex items-center gap-3">
                          {React.createElement(getCategoryIcon(category), {
                            className: "h-6 w-6 text-gray-600"
                          })}
                          <h2 className="text-xl font-extrabold text-gray-900">
                            {categoryTitles[category as keyof typeof categoryTitles]}
                          </h2>
                          <Badge variant="outline" className="bg-gray-50">
                            {items.length} {items.length === 1 ? 'item' : 'itens'}
                          </Badge>
                        </div>
                        
                        <div className={`grid gap-4 ${
                          viewMode === 'grid' 
                            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                            : 'grid-cols-1'
                        }`}>
                          {items.map((item) => {
                            const IconComponent = item.icon;
                            return (
                              <Card
                                key={item.id}
                                onClick={() => handleItemClick(item.href)}
                                className="group relative border shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer bg-gradient-to-br from-white to-gray-50 hover:from-blue-50 hover:to-white"
                              >
                                {item.isNew && (
                                  <div className="absolute top-4 right-4">
                                    <Badge className="bg-green-100 text-green-700 border-green-200">
                                      Novo
                                    </Badge>
                                  </div>
                                )}
                                
                                <CardContent className={`${viewMode === 'grid' ? 'p-6' : 'p-4'}`}>
                                  <div className={`flex items-start ${viewMode === 'grid' ? 'flex-col space-y-4' : 'space-x-4'}`}>
                                    <div className={`p-3 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl group-hover:scale-110 transition-transform duration-300 ${viewMode === 'list' ? 'flex-shrink-0' : ''}`}>
                                      <IconComponent className="h-6 w-6 text-gray-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h3 className={`font-extrabold text-gray-900 group-hover:text-blue-600 transition-colors ${viewMode === 'grid' ? 'mb-2' : 'mb-1'}`}>
                                        {item.title}
                                      </h3>
                                      <p className="text-sm text-gray-600 leading-relaxed">
                                        {item.description}
                                      </p>
                                    </div>
                                    {viewMode === 'list' && (
                                      <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between">
                        <h2 className="text-xl font-extrabold text-gray-900">
                          {searchTerm ? `Resultados para "${searchTerm}"` : categoryTitles[selectedCategory as keyof typeof categoryTitles]}
                        </h2>
                        <Badge variant="outline" className="bg-gray-50">
                          {filteredItems.length} {filteredItems.length === 1 ? 'resultado' : 'resultados'}
                        </Badge>
                      </div>
                      
                      {filteredItems.length > 0 ? (
                        <div className={`grid gap-4 ${
                          viewMode === 'grid' 
                            ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' 
                            : 'grid-cols-1'
                        }`}>
                          {filteredItems.map((item) => {
                            const IconComponent = item.icon;
                            return (
                              <Card
                                key={item.id}
                                onClick={() => handleItemClick(item.href)}
                                className="group relative border shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer bg-gradient-to-br from-white to-gray-50 hover:from-blue-50 hover:to-white"
                              >
                                {item.isNew && (
                                  <div className="absolute top-4 right-4">
                                    <Badge className="bg-green-100 text-green-700 border-green-200">
                                      Novo
                                    </Badge>
                                  </div>
                                )}
                                
                                <CardContent className={`${viewMode === 'grid' ? 'p-6' : 'p-4'}`}>
                                  <div className={`flex items-start ${viewMode === 'grid' ? 'flex-col space-y-4' : 'space-x-4'}`}>
                                    <div className={`p-3 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl group-hover:scale-110 transition-transform duration-300 ${viewMode === 'list' ? 'flex-shrink-0' : ''}`}>
                                      <IconComponent className="h-6 w-6 text-gray-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <h3 className={`font-extrabold text-gray-900 group-hover:text-blue-600 transition-colors ${viewMode === 'grid' ? 'mb-2' : 'mb-1'}`}>
                                        {item.title}
                                      </h3>
                                      <p className="text-sm text-gray-600 leading-relaxed">
                                        {item.description}
                                      </p>
                                    </div>
                                    {viewMode === 'list' && (
                                      <ArrowRight className="h-5 w-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0" />
                                    )}
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="text-center py-16">
                          <div className="space-y-4">
                            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                              <Search className="h-10 w-10 text-gray-400" />
                            </div>
                            <div className="space-y-2">
                              <h3 className="text-lg font-extrabold text-gray-900">
                                Nenhum resultado encontrado
                              </h3>
                              <p className="text-gray-500 max-w-md mx-auto">
                                Tente ajustar os filtros ou termos de pesquisa para encontrar as configurações que você procura.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Enhanced Quick Actions */}
                <div className="mt-12 bg-gradient-to-r from-blue-50 via-purple-50 to-indigo-50 rounded-2xl p-8">
                  <div className="text-center mb-8">
                    <h3 className="text-xl font-extrabold text-gray-900 mb-2">Ações Rápidas</h3>
                    <p className="text-gray-600">Acesse rapidamente as configurações mais utilizadas</p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Button
                      onClick={() => navigate('/dashboard')}
                      variant="outline"
                      size="lg"
                      className="h-16 bg-white/80 backdrop-blur-sm border-green-200 hover:bg-white hover:border-green-300 transition-all duration-300 group"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-green-100 rounded-lg group-hover:bg-green-200 transition-colors">
                          <BarChart className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="text-left">
                          <div className="font-extrabold text-gray-900">Dashboard</div>
                          <div className="text-sm text-gray-500">Métricas e relatórios</div>
                        </div>
                      </div>
                    </Button>
                    
                    <Button
                      onClick={() => navigate('/users')}
                      variant="outline"
                      size="lg"
                      className="h-16 bg-white/80 backdrop-blur-sm border-blue-200 hover:bg-white hover:border-blue-300 transition-all duration-300 group"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                        <div className="text-left">
                          <div className="font-extrabold text-gray-900">Gerenciar Usuários</div>
                          <div className="text-sm text-gray-500">Adicionar e configurar</div>
                        </div>
                      </div>
                    </Button>
                    
                    <Button
                      onClick={() => navigate('/integrations')}
                      variant="outline"
                      size="lg"
                      className="h-16 bg-white/80 backdrop-blur-sm border-purple-200 hover:bg-white hover:border-purple-300 transition-all duration-300 group"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-purple-100 rounded-lg group-hover:bg-purple-200 transition-colors">
                          <Link2 className="h-5 w-5 text-purple-600" />
                        </div>
                        <div className="text-left">
                          <div className="font-extrabold text-gray-900">Configurar Integrações</div>
                          <div className="text-sm text-gray-500">APIs e conexões</div>
                        </div>
                      </div>
                    </Button>
                    
                    <Button
                      onClick={() => navigate('/access-control')}
                      variant="outline"
                      size="lg"
                      className="h-16 bg-white/80 backdrop-blur-sm border-indigo-200 hover:bg-white hover:border-indigo-300 transition-all duration-300 group"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="p-2 bg-indigo-100 rounded-lg group-hover:bg-indigo-200 transition-colors">
                          <Shield className="h-5 w-5 text-indigo-600" />
                        </div>
                        <div className="text-left">
                          <div className="font-extrabold text-gray-900">Controle de Acesso</div>
                          <div className="text-sm text-gray-500">Permissões e roles</div>
                        </div>
                      </div>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}